import { useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "@/common/ui/empty";
import { Skeleton } from "@/common/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/ui/tabs";

import { AddOrgUnitModal } from "./components/AddOrgUnitModal";
import { CreateOrgModal } from "./components/CreateOrgModal";
import { OrgBreadcrumb } from "./components/OrgBreadcrumb";
import { OrgTree } from "./components/OrgTree";
import { OrgUnitDetailPanel } from "./components/OrgUnitDetailPanel";
import { RolesSection } from "./components/RolesSection";
import type { OrgUnit } from "./types";
import { orgDetailKey, useOrgDetail } from "./use-org-detail";
import { orgTreeKey, useOrgTree } from "./use-org-tree";
import { useSession } from "./useSession";

function findNodeById(tree: OrgUnit, id: string): OrgUnit | null {
  if (tree.id === id) return tree;
  for (const child of tree.children) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
}

export function OrganizationPage(): React.ReactElement {
  const { session, isLoading: sessionLoading, refreshSession } = useSession();
  const queryClient = useQueryClient();

  const [selectedNode, setSelectedNode] = useState<OrgUnit | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addParentId, setAddParentId] = useState<string | null>(null);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);

  const activeOrgUnit = session?.activeOrgUnit;

  /* Selection belongs to the org it was made in, and the switch is visible
     DURING render, not after it. Clearing in an effect leaves one painted frame
     where the tree already belongs to the new org (a cached key resolves
     immediately) while the detail sheet still shows the previous org's unit and
     members. Deriving closes that frame; the state below only persists what is
     derived here, so a later render does not re-open it. */
  const [selectionOrgId, setSelectionOrgId] = useState(activeOrgUnit?.id);
  const orgChanged = selectionOrgId !== activeOrgUnit?.id;
  if (orgChanged) {
    setSelectionOrgId(activeOrgUnit?.id);
    setSelectedNode(null);
    setDetailOpen(false);
  }
  const shownNode = orgChanged ? null : selectedNode;
  const shownDetailOpen = orgChanged ? false : detailOpen;

  const { tree, isLoading: treeLoading, error } = useOrgTree(activeOrgUnit?.id);
  const {
    members,
    invitations,
    isLoading: detailLoading,
    error: detailError,
  } = useOrgDetail(shownNode?.id);

  const availableRoles = session?.availableRoles ?? [];
  const hasNoOrg = !sessionLoading && !activeOrgUnit;
  const isLoading = sessionLoading || treeLoading;

  const handleSelectNode = (node: OrgUnit): void => {
    setSelectedNode(node);
    setDetailOpen(true);
  };

  const handleAddChild = (parentId: string): void => {
    setAddParentId(parentId);
    setAddModalOpen(true);
  };

  const invalidateOrgData = (): void => {
    if (activeOrgUnit?.id) {
      void queryClient.invalidateQueries({ queryKey: orgTreeKey(activeOrgUnit.id) });
    }
    if (selectedNode) {
      void queryClient.invalidateQueries({ queryKey: orgDetailKey(selectedNode.id) });
    }
  };

  const parentNode = addParentId && tree ? findNodeById(tree, addParentId) : null;

  let content: React.ReactElement;

  if (isLoading) {
    content = (
      <div data-testid="org-page-loading" className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="ml-5 h-6 w-[90%]" />
            <Skeleton className="ml-10 h-6 w-[80%]" />
            <Skeleton className="ml-5 h-6 w-[85%]" />
          </CardContent>
        </Card>
      </div>
    );
  } else if (hasNoOrg) {
    content = (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-auto p-6">
          <Card className="mx-auto mt-12 max-w-lg">
            <CardContent className="pt-6">
              <Empty>
                <EmptyMedia variant="icon">
                  <Building2 className="h-6 w-6" />
                </EmptyMedia>
                <EmptyTitle>No organization yet</EmptyTitle>
                <EmptyDescription>
                  Create your first enterprise to get started with organization management.
                </EmptyDescription>
                <Button onClick={() => setCreateOrgOpen(true)}>Create Enterprise</Button>
              </Empty>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  } else if (error) {
    content = (
      <Card className="mx-auto mt-12 max-w-lg">
        <CardContent className="pt-6">
          <Empty>
            <EmptyMedia variant="icon">
              <Building2 className="h-6 w-6" />
            </EmptyMedia>
            <EmptyTitle>Failed to load</EmptyTitle>
            <EmptyDescription>{error}</EmptyDescription>
            <Button
              onClick={() =>
                activeOrgUnit?.id
                  ? void queryClient.invalidateQueries({ queryKey: orgTreeKey(activeOrgUnit.id) })
                  : void refreshSession()
              }
            >
              Retry
            </Button>
          </Empty>
        </CardContent>
      </Card>
    );
  } else if (!tree) {
    content = <></>;
  } else {
    const isEmpty = tree.children.length === 0;

    content = (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-auto p-6">
          {selectedNode && (
            <div className="mb-4 flex justify-end">
              <OrgBreadcrumb
                selectedNode={selectedNode}
                tree={tree}
                onNavigate={handleSelectNode}
              />
            </div>
          )}
          <Tabs defaultValue="structure" className="space-y-4">
            <TabsList>
              <TabsTrigger value="structure" data-testid="org-tab-structure">
                Structure
              </TabsTrigger>
              <TabsTrigger value="roles" data-testid="org-tab-roles">
                Roles
              </TabsTrigger>
            </TabsList>

            <TabsContent value="structure">
              {isEmpty ? (
                <Card className="mx-auto mt-12 max-w-lg">
                  <CardContent className="pt-6">
                    <Empty>
                      <EmptyMedia variant="icon">
                        <Building2 className="h-6 w-6" />
                      </EmptyMedia>
                      <EmptyTitle>No organization structure yet</EmptyTitle>
                      <EmptyDescription>
                        Start building your organization by adding divisions, departments, or teams.
                      </EmptyDescription>
                      <Button onClick={() => handleAddChild(tree.id)}>Add First Unit</Button>
                    </Empty>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Organization Tree</CardTitle>
                    <CardDescription>
                      Click on a unit to view details. Use the + button to add child units.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <OrgTree
                      tree={tree}
                      selectedId={selectedNode?.id ?? null}
                      onSelect={handleSelectNode}
                      onAddChild={handleAddChild}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="roles">
              {/* Keyed by enterprise so a switch gives a FRESH instance. A
                  cached tree no longer routes this page through a loading
                  branch, so without the key the same instance survives the
                  switch holding `createOpen`, `editTarget` and `deleteTarget`,
                  and an edit or delete then targets the previous enterprise's
                  role over the new enterprise's list. A key resets whatever
                  state the component holds, including any added later, which
                  naming the three fields would not. */}
              {activeOrgUnit && (
                <RolesSection
                  key={activeOrgUnit.enterprise_id}
                  enterpriseId={activeOrgUnit.enterprise_id}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        <OrgUnitDetailPanel
          node={shownNode}
          open={shownDetailOpen}
          onOpenChange={setDetailOpen}
          members={members}
          invitations={invitations}
          isLoading={detailLoading}
          detailError={detailError}
          availableRoles={availableRoles}
          onDataChanged={invalidateOrgData}
        />

        {parentNode && (
          <AddOrgUnitModal
            open={addModalOpen}
            onOpenChange={setAddModalOpen}
            enterpriseId={parentNode.enterprise_id}
            parentId={parentNode.id}
            parentName={parentNode.name}
            onUnitCreated={invalidateOrgData}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {content}
      <CreateOrgModal
        open={createOrgOpen}
        onOpenChange={setCreateOrgOpen}
        onOrgCreated={() => void refreshSession()}
      />
    </>
  );
}
